import { useT, type Locale, type T } from "@/i18n";
import { shortDay, useFormat } from "@/lib/format";
import type { Receipt, ReceptionResult, ReceptionRow } from "@/services/receiving";

const TAG: Record<Locale, string> = { fr: "fr-CH", en: "en-GB", de: "de-CH", it: "it-CH" };

// His `dayLbl`: the short weekday and dd.mm — "mar 06.10".
function dayLbl(iso: string | null | undefined, locale: Locale) {
  if (!iso) return "";
  const wd = new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(TAG[locale], { weekday: "short" }).replace(".", "");
  return `${wd} ${shortDay(iso).slice(0, 5)}`;
}

// Singular or plural for a count, by each language's own rule: French says "0 ligne lue" (and his
// English "0 line read", `n>1`); German and Italian put 0 in the plural ("0 Zeilen gelesen", "0 righe lette").
export const isMany = (n: number, locale: Locale) => (locale === "de" || locale === "it" ? n !== 1 : n > 1);
// "{n} …" from the key's `One` or `Many` form, as the language counts n.
export const countText = (t: T, locale: Locale, key: string, n: number) => t(`${key}${isMany(n, locale) ? "Many" : "One"}`, { n });

// His `lbl` (as the job line's furnitureType): the furniture type, singular or plural; unknown as is.
function furnitureType(t: T, slug: string, n: number) {
  const key = `furn.${n > 1 ? "many" : "one"}.${slug}`;
  const label = t(key);
  return label === key ? slug : label;
}

// His `recHead`: what the document is, from whom, its number and date, when it was read.
export function ReceiptHead({ receipt }: { receipt: Receipt }) {
  const { t } = useT();
  const { dateTime } = useFormat();
  const kind = t(`note.doc.${receipt.document_type ?? "other"}`);
  return (
    <span>
      <b className="font-semibold text-slate-800">{kind}</b>
      {receipt.supplier_name && <> · {receipt.supplier_name}</>}
      {receipt.carrier && receipt.carrier !== receipt.supplier_name && <> · {receipt.carrier}</>}
      {receipt.document_reference && <> · n° {receipt.document_reference}</>}
      {receipt.document_date && <> · {shortDay(receipt.document_date)}</>}
      {receipt.read_at && (
        <span className="ml-2 text-xs text-slate-400">
          {t("note.readAt").replace("{when}", dateTime(receipt.read_at))}
          {receipt.pages_count ? ` · ${t(receipt.pages_count > 1 ? "note.pagesMany" : "note.pagesOne", { n: receipt.pages_count })}` : ""}
        </span>
      )}
    </span>
  );
}

// His `recResultHtml`: one block per client delivery, then the lines that found no client, by why.
export function ReceptionResultView({ result, onOpenJob, compact = false }: {
  result: ReceptionResult; onOpenJob?: (eventId: number) => void; compact?: boolean;
}) {
  const { t, locale } = useT();
  const { none = [], many = [], unread = [] } = result.unmatched;
  return (
    <div className="space-y-3">
      {result.blocks.map((b) => (
        <div key={b.event.id} className="rounded-lg border border-slate-200 bg-white">
          {!compact && (
            <div className="flex flex-wrap items-baseline gap-x-2 border-b border-slate-100 px-3 py-2">
              <b className="text-sm font-semibold text-slate-800">{b.event.client_name || `#${b.event.id}`}</b>
              {/* His header: "montage <day>", the job's own day, and its warehouse. */}
              <span className="text-xs text-slate-500">
                {t("note.asmOn", { d: dayLbl(b.event.day ?? b.event.starts_at, locale) })}
                {b.event.warehouse && ` · ${b.event.warehouse.name}`}
              </span>
              {onOpenJob && (
                <button onClick={() => onOpenJob(b.event.id)}
                  className="ml-auto rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50">
                  {t("note.open")}
                </button>
              )}
            </div>
          )}
          {/* His `recRowsHtml`: an order confirmation says so in every client's block. */}
          {!result.proves_arrival && (
            <p className="mx-3 mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">{t("note.ocW")}</p>
          )}
          <ul className="divide-y divide-slate-100">
            {b.rows.map((r, i) => <Row key={`${r.product_id ?? r.name}-${i}`} row={r} arrival={result.proves_arrival} />)}
          </ul>
        </div>
      ))}

      {many.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm">
          <b className="text-amber-900">{t("note.many")}</b>
          <ul className="mt-1 space-y-0.5">
            {many.map((l) => (
              <li key={l.id} className="text-slate-700">
                <LineText line={l} /> →{" "}
                {l.clients.map((c) => `${c.client_name ?? "?"} (${c.contract_name ?? "?"}, ${shortDay(c.starts_at)})`).join(" / ")}
              </li>
            ))}
          </ul>
        </div>
      )}
      {none.length > 0 && (
        <details className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
          <summary className="cursor-pointer text-slate-700">{t("note.un")} ({none.length})</summary>
          <ul className="mt-1 space-y-0.5">{none.map((l) => <li key={l.id}><LineText line={l} /></li>)}</ul>
        </details>
      )}
      {(unread.length > 0 || result.unclear.length > 0) && (
        <div className="rounded-lg border border-red-200 bg-red-50/60 px-3 py-2 text-sm">
          <b className="text-red-900">{t("note.unclear")}</b>
          <ul className="mt-1 space-y-0.5 text-slate-700">
            {unread.map((l) => <li key={`u-${l.id}`}>{t("note.unread")} : <LineText line={l} /></li>)}
            {result.unclear.map((u, i) => <li key={`c-${i}`}>{u}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function LineText({ line }: { line: { no: string | null; desc: string | null; qty: number | null; ref: string | null } }) {
  return (
    <>
      <span className="font-mono text-xs">{line.no}</span> {line.desc}
      {line.qty != null && ` ×${line.qty}`}
      {line.ref && <span className="text-xs text-slate-400"> ({line.ref})</span>}
    </>
  );
}

// His row icons: ✅ complete · ⬜ not on this note · ➕ extra · ⚠️ short — or 📝 for an order
// confirmation, which says what was ordered and never that it arrived.
function Row({ row, arrival }: { row: ReceptionRow; arrival: boolean }) {
  const { t, locale } = useT();
  const icon = !arrival ? (row.status === "not_on_note" ? "⬜" : "📝")
    : row.status === "complete" && !row.cartons_incomplete ? "✅"
    : row.status === "not_on_note" ? "⬜" : row.status === "extra" ? "➕" : "⚠️";
  const state = !arrival ? (row.status === "not_on_note" ? t("note.miss") : t("note.confSt"))
    : row.status === "complete" ? t("note.ok")
    // "1 fehlt" / "2 fehlen", "manca 1" / "mancano 2"; "en trop", "extra", "zu viel", "in più" never change.
    : row.status === "short" ? countText(t, locale, "note.short", row.ordered - row.received)
    : row.status === "extra" ? t("note.extra", { n: row.received - row.ordered })
    : t("note.miss");
  const dim = row.status === "not_on_note";
  return (
    <li className={`flex gap-2 px-3 py-2 ${dim ? "opacity-60" : ""}`}>
      <span aria-hidden="true">{icon}</span>
      <div className="min-w-0 flex-1 text-sm">
        {/* His row: the title, then in grey the furniture type for the quantity ordered and the order. */}
        <div className="text-slate-800">
          {row.name || (row.product_type_slug ? furnitureType(t, row.product_type_slug, 1) : "")}{" "}
          <span className="text-xs text-slate-400">
            {[row.product_type_slug && furnitureType(t, row.product_type_slug, row.ordered),
              row.order_id && `${t("job.order")} #${row.order_id}`].filter(Boolean).join(" · ")}
          </span>
        </div>
        <div className="text-xs text-slate-600">
          {t("note.ord")} <b>{row.ordered}</b> · {arrival ? t("note.got") : t("note.conf")} <b>{row.received}</b> · {state}
          {row.cartons > 1 && ` · ${t("job.cartons", { n: row.cartons })}`}
        </div>
        {row.parts.length > 0 ? (
          <div className="text-xs text-slate-500">
            {row.parts.map((p) => `${p.no}${p.qty ? ` ×${p.qty}` : ""}`).join(" · ")}
          </div>
        ) : row.colli ? (
          <div className="text-xs text-slate-500">{t(row.colli > 1 ? "note.colliMany" : "note.colliOne", { n: row.colli })}</div>
        ) : null}
        {row.cartons_incomplete && <div className="text-xs font-medium text-amber-800">⚠️ {t("note.cw")}</div>}
      </div>
    </li>
  );
}
